import React, { useEffect, useRef, useState } from "react";
import api from "../../utils/api";
import { toast } from "react-toastify";
import { formatPrice } from "../../utils/helpers";
import AdminNav from "../../components/AdminNav";
import { normalizePaymentMethods } from "../../utils/paymentMethods";
import { SYSTEM_PRODUCT_BANNER_IDS, normalizeProductBanners } from "../../utils/productBanners";
import { normalizeShippingMethods } from "../../utils/shippingMethods";
import { normalizeProductOptionDefinitions, normalizeProductOptionValues } from "../../utils/productOptions";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productBanners, setProductBanners] = useState({});
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [productOptionSets, setProductOptionSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isBulkEditMode, setIsBulkEditMode] = useState(false);
  const [bulkEditProductIds, setBulkEditProductIds] = useState([]);
  const [formData, setFormData] = useState({
    sku: "",
    name: "",
    description: "",
    price: "",
    stockLevel: 0,
    categoryIds: [],
    productBannerIds: [],
    productPaymentMethodIds: [],
    productShippingMethodIds: [],
    productOptionValues: {},
    gender: "unisex",
    status: "active",
    releaseDate: "",
    imageUrls: [], // Array of R2 image URLs
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState([]);
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  const [browsableFolders, setBrowsableFolders] = useState([]);
  const [browsableFiles, setBrowsableFiles] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedBrowseImages, setSelectedBrowseImages] = useState(new Set());
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [hoveredProductImage, setHoveredProductImage] = useState(null);
  const [currentPath, setCurrentPath] = useState("");
  const [requestedPath, setRequestedPath] = useState(""); // Track the originally requested path
  const imageInputRef = useRef(null);

  const [filters, setFilters] = useState({
    status: "",
    category: "",
    search: "",
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchProductConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("status", filters.status || "all");
      if (filters.category) params.append("category", filters.category);
      if (filters.search) params.append("search", filters.search);
      params.append("limit", "100");

      const { data } = await api.get(`/products?${params}`);
      setProducts(data.products);
      setSelectedProductIds((current) => {
        const fetchedProductIds = new Set(data.products.map((product) => product.id));
        return new Set([...current].filter((productId) => fetchedProductIds.has(productId)));
      });
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await api.get("/categories/all");
      setCategories(data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchProductConfiguration = async () => {
    try {
      const { data } = await api.get("/admin/settings/site");
      setProductBanners(normalizeProductBanners(data.settings?.product_banners));
      setPaymentMethods(normalizePaymentMethods(data.settings?.payment_methods || []));
      setShippingMethods(normalizeShippingMethods(data.settings?.shipping_methods || []));
      setProductOptionSets(normalizeProductOptionDefinitions(data.settings?.product_option_sets || []));
    } catch (error) {
      console.error("Error fetching product configuration:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(categoryId) 
        ? prev.categoryIds.filter((id) => id !== categoryId) 
        : [...prev.categoryIds, categoryId],
    }));
  };

  const handleProductOptionValueToggle = (optionSetId, optionValue) => {
    setFormData((prev) => {
      const currentValues = prev.productOptionValues[optionSetId] || [];
      const nextValues = currentValues.includes(optionValue)
        ? currentValues.filter((value) => value !== optionValue)
        : [...currentValues, optionValue];
      const nextProductOptionValues = { ...prev.productOptionValues };

      if (nextValues.length > 0) {
        nextProductOptionValues[optionSetId] = nextValues;
      } else {
        delete nextProductOptionValues[optionSetId];
      }

      return {
        ...prev,
        productOptionValues: nextProductOptionValues,
      };
    });
  };

  const handleProductBannerToggle = (bannerId) => {
    setFormData((prev) => ({
      ...prev,
      productBannerIds: prev.productBannerIds.includes(bannerId)
        ? prev.productBannerIds.filter((id) => id !== bannerId)
        : [...prev.productBannerIds, bannerId],
    }));
  };

  const handleProductShippingMethodToggle = (shippingMethodId) => {
    setFormData((prev) => ({
      ...prev,
      productShippingMethodIds: prev.productShippingMethodIds.includes(shippingMethodId)
        ? prev.productShippingMethodIds.filter((id) => id !== shippingMethodId)
        : [...prev.productShippingMethodIds, shippingMethodId],
    }));
  };

  const handleProductPaymentMethodToggle = (paymentMethodId) => {
    setFormData((prev) => ({
      ...prev,
      productPaymentMethodIds: prev.productPaymentMethodIds.includes(paymentMethodId)
        ? prev.productPaymentMethodIds.filter((id) => id !== paymentMethodId)
        : [...prev.productPaymentMethodIds, paymentMethodId],
    }));
  };

  const validateProductForm = () => {
    if (!formData.sku.trim()) {
      toast.error("SKU is required");
      return false;
    }

    if (!formData.name.trim()) {
      toast.error("Product name is required");
      return false;
    }

    if (!formData.description.trim()) {
      toast.error("Description is required");
      return false;
    }

    if (!Number.isFinite(parseFloat(formData.price)) || parseFloat(formData.price) < 0) {
      toast.error("Price must be a valid amount");
      return false;
    }

    if (!Number.isFinite(parseInt(formData.stockLevel, 10)) || parseInt(formData.stockLevel, 10) < 0) {
      toast.error("Stock level must be a valid whole number");
      return false;
    }

    if (!formData.gender) {
      toast.error("Gender is required");
      return false;
    }

    if (!formData.status) {
      toast.error("Status is required");
      return false;
    }

    if ((formData.status === "coming_soon" || formData.status === "pre_order") && !formData.releaseDate) {
      toast.error("Release date is required for the selected status");
      return false;
    }

    if (formData.categoryIds.length === 0) {
      toast.error("Select at least one category");
      return false;
    }

    if (formData.productPaymentMethodIds.length === 0) {
      toast.error("Select at least one payment method");
      return false;
    }

    if (formData.productShippingMethodIds.length === 0) {
      toast.error("Select at least one shipping method");
      return false;
    }

    if (formData.imageUrls.length === 0) {
      toast.error("Please upload at least one product image");
      return false;
    }

    return true;
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (formData.imageUrls.length + files.length > 5) {
      toast.error("Maximum 5 images allowed per product");
      return;
    }

    if (!formData.sku) {
      toast.error("Please enter a SKU before uploading images");
      return;
    }

    setUploadingImage(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append("image", file);
        
        // Extract base SKU folder from SKU (e.g., F2794-2 -> 2794)
        const baseSku = formData.sku.match(/\d+/)?.[0] || "misc";
        formDataUpload.append("folder", `${baseSku}/${formData.sku}`);

        const response = await api.post("/upload/image", formDataUpload, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        return response.data.url;
      });

      const uploadedUrls = await Promise.all(uploadPromises);

      setFormData((prev) => ({
        ...prev,
        imageUrls: [...prev.imageUrls, ...uploadedUrls],
      }));

      setImagePreview((prev) => [...prev, ...uploadedUrls]);
      toast.success(`${files.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload images");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async (index) => {
    const imageUrl = formData.imageUrls[index];

    try {
      // Delete from R2
      await api.delete("/upload/image", { data: { url: imageUrl } });

      // Remove from state
      setFormData((prev) => ({
        ...prev,
        imageUrls: prev.imageUrls.filter((_, i) => i !== index),
      }));
      setImagePreview((prev) => prev.filter((_, i) => i !== index));

      toast.success("Image removed");
    } catch (error) {
      console.error("Error removing image:", error);
      toast.error("Failed to remove image");
    }
  };

  const handleBrowseImages = async () => {
    setShowImageBrowser(true);
    
    // Auto-navigate to SKU folder if SKU is set
    let targetPath = "";
    if (formData.sku) {
      const baseSku = formData.sku.match(/\d+/)?.[0];
      if (baseSku) {
        targetPath = `${baseSku}/${formData.sku}/`;
      }
    }
    
    setRequestedPath(targetPath);
    await fetchBrowserContentWithFallback(targetPath);
  };

  const openImageUploadDialog = () => {
    if (!formData.sku) {
      toast.error("Please enter a SKU before uploading images");
      return;
    }

    imageInputRef.current?.click();
  };

  const fetchBrowserContent = async (path) => {
    setLoadingImages(true);

    try {
      const { data } = await api.get(`/upload/browse?prefix=${encodeURIComponent(path)}`);
      setBrowsableFolders(data.folders || []);
      setBrowsableFiles(data.files || []);
      setCurrentPath(path);
    } catch (error) {
      console.error("Error loading images:", error);
      toast.error("Failed to load images");
    } finally {
      setLoadingImages(false);
    }
  };

  const fetchBrowserContentWithFallback = async (path) => {
    setLoadingImages(true);

    try {
      const { data } = await api.get(`/upload/browse?prefix=${encodeURIComponent(path)}`);
      const hasFolders = data.folders && data.folders.length > 0;
      const hasFiles = data.files && data.files.length > 0;

      // If the target path has content, use it
      if (hasFolders || hasFiles) {
        setBrowsableFolders(data.folders || []);
        setBrowsableFiles(data.files || []);
        setCurrentPath(path);
        setLoadingImages(false);
        return;
      }

      // If empty, try parent directories
      if (path) {
        const parts = path.split("/").filter(Boolean);
        
        // Try each parent level
        while (parts.length > 0) {
          parts.pop(); // Remove last part
          const parentPath = parts.length > 0 ? parts.join("/") + "/" : "";
          
          try {
            const { data: parentData } = await api.get(`/upload/browse?prefix=${encodeURIComponent(parentPath)}`);
            const parentHasFolders = parentData.folders && parentData.folders.length > 0;
            const parentHasFiles = parentData.files && parentData.files.length > 0;
            
            if (parentHasFolders || parentHasFiles) {
              setBrowsableFolders(parentData.folders || []);
              setBrowsableFiles(parentData.files || []);
              setCurrentPath(parentPath);
              toast.info(path ? `Folder '${path}' not found. Showing parent directory.` : "Showing root directory");
              setLoadingImages(false);
              return;
            }
          } catch (err) {
            console.error("Error checking parent:", err);
          }
        }
      }

      // Fallback to root if nothing found
      setBrowsableFolders([]);
      setBrowsableFiles([]);
      setCurrentPath("");
      toast.info("No images found. Showing root directory.");
    } catch (error) {
      console.error("Error loading images:", error);
      toast.error("Failed to load images");
      // Fallback to root
      setBrowsableFolders([]);
      setBrowsableFiles([]);
      setCurrentPath("");
    } finally {
      setLoadingImages(false);
    }
  };

  const navigateToFolder = async (folderPath) => {
    await fetchBrowserContent(folderPath);
  };

  const navigateUp = async () => {
    // Remove the last folder from the path
    const parts = currentPath.split("/").filter(Boolean);
    parts.pop();
    const newPath = parts.length > 0 ? parts.join("/") + "/" : "";
    await fetchBrowserContent(newPath);
  };

  const navigateToBreadcrumb = async (index) => {
    const parts = currentPath.split("/").filter(Boolean);
    const newPath = index === -1 ? "" : parts.slice(0, index + 1).join("/") + "/";
    await fetchBrowserContent(newPath);
  };

  const toggleBrowseImageSelection = (imageUrl) => {
    const newSelected = new Set(selectedBrowseImages);
    if (newSelected.has(imageUrl)) {
      newSelected.delete(imageUrl);
    } else {
      // Check if we're at the limit
      if (formData.imageUrls.length + newSelected.size >= 5) {
        toast.error("Maximum 5 images allowed per product");
        return;
      }
      newSelected.add(imageUrl);
    }
    setSelectedBrowseImages(newSelected);
  };

  const handleAddSelectedImages = () => {
    const newImages = Array.from(selectedBrowseImages);

    if (formData.imageUrls.length + newImages.length > 5) {
      toast.error("Maximum 5 images allowed per product");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      imageUrls: [...prev.imageUrls, ...newImages],
    }));

    setImagePreview((prev) => [...prev, ...newImages]);
    setShowImageBrowser(false);
    setSelectedBrowseImages(new Set());
    toast.success(`${newImages.length} image(s) added`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Bulk edit mode - only validate payment and shipping methods
    if (isBulkEditMode) {
      if (formData.productPaymentMethodIds.length === 0) {
        toast.error("Select at least one payment method");
        return;
      }

      if (formData.productShippingMethodIds.length === 0) {
        toast.error("Select at least one shipping method");
        return;
      }

      try {
        const payload = {
          productPaymentMethodIds: Array.from(new Set(formData.productPaymentMethodIds)),
          productShippingMethodIds: Array.from(new Set(formData.productShippingMethodIds)),
        };

        // Update all selected products
        const results = await Promise.allSettled(
          bulkEditProductIds.map((id) => api.put(`/products/${id}`, payload))
        );

        const successCount = results.filter((result) => result.status === "fulfilled").length;
        const failedCount = results.length - successCount;

        if (successCount > 0) {
          toast.success(
            `${successCount} product${successCount === 1 ? "" : "s"} updated successfully`
          );
        }

        if (failedCount > 0) {
          toast.error(
            `${failedCount} product${failedCount === 1 ? "" : "s"} could not be updated`
          );
        }

        setShowModal(false);
        setIsBulkEditMode(false);
        setBulkEditProductIds([]);
        resetForm();
        fetchProducts();
      } catch (error) {
        console.error("Error updating products:", error);
        toast.error("Failed to update products");
      }
      return;
    }

    // Single product edit mode
    if (!validateProductForm()) {
      return;
    }

    try {
      const selectedBannerIds = Array.from(new Set(formData.productBannerIds));
      const payload = {
        ...formData,
        isFeatured: selectedBannerIds.includes("featured"),
        isNewIn: selectedBannerIds.includes("new_arrival"),
        price: parseFloat(formData.price),
        stockLevel: parseInt(formData.stockLevel, 10) || 0,
        categoryIds: formData.categoryIds.map(id => parseInt(id)),
        productBannerIds: selectedBannerIds.filter((bannerId) => !SYSTEM_PRODUCT_BANNER_IDS.includes(bannerId)),
        productPaymentMethodIds: Array.from(new Set(formData.productPaymentMethodIds)),
        productShippingMethodIds: Array.from(new Set(formData.productShippingMethodIds)),
        productOptionValues: normalizeProductOptionValues(formData.productOptionValues),
      };

      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, payload);
        toast.success("Product updated successfully");
      } else {
        await api.post("/products", payload);
        toast.success("Product created successfully");
      }

      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error(error.response?.data?.error || "Failed to save product");
    }
  };

  const handleEdit = (product) => {
    // Check if multiple products are selected
    if (selectedProductIds.size > 1) {
      // Bulk edit mode - only payment and shipping methods
      setIsBulkEditMode(true);
      setBulkEditProductIds(Array.from(selectedProductIds));
      setFormData({
        sku: "",
        name: "",
        description: "",
        price: "",
        stockLevel: 0,
        categoryIds: [],
        productBannerIds: [],
        productPaymentMethodIds: paymentMethods.map((method) => method.id),
        productShippingMethodIds: shippingMethods.map((method) => method.id),
        productOptionValues: {},
        gender: "unisex",
        status: "active",
        releaseDate: "",
        imageUrls: [],
      });
    } else {
      // Single product edit mode
      setIsBulkEditMode(false);
      setBulkEditProductIds([]);
      setEditingProduct(product);
      const existingImages = product.images?.map((img) => img.url) || [];
      const productCategories = product.categories?.map(cat => cat.id) || [];
      setFormData({
        sku: product.sku,
        name: product.name,
        description: product.description || "",
        price: product.price,
        stockLevel: product.stock_level ?? 0,
        categoryIds: productCategories,
        productBannerIds: product.selected_banner_ids || [],
        productPaymentMethodIds: product.product_payment_method_ids?.length ? product.product_payment_method_ids : paymentMethods.map((method) => method.id),
        productShippingMethodIds: product.product_shipping_method_ids || [],
        productOptionValues: normalizeProductOptionValues(product.product_option_values || {}),
        gender: product.gender || "unisex",
        status: product.status,
        releaseDate: product.release_date || "",
        imageUrls: existingImages,
      });
      setImagePreview(existingImages);
    }
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return;
    }

    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    }
  };

  const handleProductSelection = (id, isSelected) => {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      if (isSelected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSelectAllProducts = (isSelected) => {
    setSelectedProductIds(() => {
      if (!isSelected) {
        return new Set();
      }
      return new Set(products.map((product) => product.id));
    });
  };

  const handleBulkDelete = async () => {
    const idsToDelete = Array.from(selectedProductIds);
    if (idsToDelete.length === 0) {
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${idsToDelete.length} selected product${idsToDelete.length === 1 ? "" : "s"}?`)) {
      return;
    }

    const results = await Promise.allSettled(idsToDelete.map((id) => api.delete(`/products/${id}`)));
    const deletedCount = results.filter((result) => result.status === "fulfilled").length;
    const failedCount = results.length - deletedCount;

    if (deletedCount > 0) {
      toast.success(`${deletedCount} product${deletedCount === 1 ? "" : "s"} deleted successfully`);
    }

    if (failedCount > 0) {
      toast.error(`${failedCount} product${failedCount === 1 ? "" : "s"} could not be deleted`);
    }

    setSelectedProductIds(new Set());
    fetchProducts();
  };

  const resetForm = () => {
    setEditingProduct(null);
    setIsBulkEditMode(false);
    setBulkEditProductIds([]);
    setFormData({
      sku: "",
      name: "",
      description: "",
      price: "",
      stockLevel: 0,
      categoryIds: [],
      productBannerIds: [],
      productPaymentMethodIds: [],
      productShippingMethodIds: [],
      productOptionValues: {},
      gender: "unisex",
      status: "active",
      releaseDate: "",
      imageUrls: [],
    });
    setImagePreview([]);
  };

  const genderOptions = ["mens", "womens", "unisex"];
  const statusOptions = ["active", "discontinued", "coming_soon", "pre_order"];
  const selectedProductCount = selectedProductIds.size;
  const allProductsSelected = products.length > 0 && selectedProductCount === products.length;
  const getProductThumbnailUrl = (product) => {
    const image = product.images?.find((img) => img.is_primary) || product.images?.[0];
    if (!image?.url) {
      return null;
    }
    return image.url.startsWith("http") ? image.url : image.url;
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <AdminNav />
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <h1 className="heading-metallic text-4xl">Product Management</h1>
            <div className="flex items-center gap-3">
              {selectedProductCount > 1 && (
                <button 
                  onClick={() => handleEdit(null)} 
                  className="bg-blue-700 hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded transition-colors"
                >
                  ✎ Edit Shipping & Payment ({selectedProductCount})
                </button>
              )}
              {selectedProductCount > 0 && (
                <button onClick={handleBulkDelete} className="bg-red-700 hover:bg-red-600 text-white font-semibold px-4 py-2 rounded transition-colors">
                  Delete selected ({selectedProductCount})
                </button>
              )}
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="btn-primary"
              >
                + Add Product
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input type="text" placeholder="Search products..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="input-field" />
              <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="input-field">
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.slug}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="input-field">
                <option value="">All Status</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status.replace("_", " ").toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Products Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="text-gold-500">Loading products...</div>
            </div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="p-4">
                      <input
                        type="checkbox"
                        checked={allProductsSelected}
                        disabled={products.length === 0}
                        onChange={(e) => handleSelectAllProducts(e.target.checked)}
                        aria-label="Select all products"
                      />
                    </th>
                    <th className="text-left p-4">Photo</th>
                    <th className="text-left p-4">SKU</th>
                    <th className="text-left p-4">Name</th>
                    <th className="text-left p-4">Categories</th>
                    <th className="text-left p-4">Price</th>
                    <th className="text-left p-4">Stock</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Featured</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => {
                    const thumbnailUrl = getProductThumbnailUrl(product);

                    return (
                      <tr key={product.id} className="border-b border-gray-800 hover:bg-gray-900">
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selectedProductIds.has(product.id)}
                            onChange={(e) => handleProductSelection(product.id, e.target.checked)}
                            aria-label={`Select ${product.name}`}
                          />
                        </td>
                        <td className="p-4">
                          {thumbnailUrl ? (
                            <img
                              src={thumbnailUrl}
                              alt={product.name}
                              className="h-12 w-12 rounded border border-gray-700 object-cover"
                              onMouseEnter={(e) => setHoveredProductImage({ url: thumbnailUrl, name: product.name, x: e.clientX, y: e.clientY })}
                              onMouseMove={(e) => setHoveredProductImage((current) => current && { ...current, x: e.clientX, y: e.clientY })}
                              onMouseLeave={() => setHoveredProductImage(null)}
                            />
                          ) : (
                            <div className="flex h-12 w-12 items-center justify-center rounded border border-gray-800 bg-gray-900 text-xs text-gray-500">
                              No img
                            </div>
                          )}
                        </td>
                        <td className="p-4 font-mono text-sm">{product.sku}</td>
                        <td className="p-4">
                          <div className="font-medium">{product.name}</div>
                        </td>
                        <td className="p-4">
                          {product.categories && product.categories.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {product.categories.map((cat) => (
                                <span key={cat.id} className="px-2 py-1 bg-gray-800 text-gray-300 rounded text-xs">
                                  {cat.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-500 text-sm">No categories</span>
                          )}
                        </td>
                        <td className="p-4 text-gold-500 font-bold">{formatPrice(product.price)}</td>
                        <td className="p-4">
                          <span className={`font-medium ${product.stock_level > 0 ? "text-white" : "text-red-400"}`}>{product.stock_level ?? 0}</span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${product.status === "active" ? "bg-green-900 text-green-300" : product.status === "discontinued" ? "bg-red-900 text-red-300" : "bg-blue-900 text-blue-300"}`}>{product.status}</span>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            {product.is_featured && <span className="px-2 py-1 bg-gold-900 text-gold-300 rounded text-xs">Featured</span>}
                            {product.is_new_in && <span className="px-2 py-1 bg-purple-900 text-purple-300 rounded text-xs">New</span>}
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <button onClick={() => handleEdit(product)} className="text-gold-500 hover:text-gold-400 mr-4">
                            Edit
                          </button>
                          <button onClick={() => handleDelete(product.id)} className="text-red-500 hover:text-red-400">
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {products.length === 0 && <div className="text-center py-12 text-gray-500">No products found</div>}
            </div>
          )}

          {hoveredProductImage && (
            <div
              className="pointer-events-none fixed z-[100] rounded-lg border border-gold-600 bg-black p-2 shadow-2xl shadow-black/60"
              style={{
                left: Math.min(hoveredProductImage.x + 18, window.innerWidth - 280),
                top: Math.min(hoveredProductImage.y + 18, window.innerHeight - 280),
              }}
            >
              <img src={hoveredProductImage.url} alt={hoveredProductImage.name} className="max-h-64 max-w-64 rounded object-contain" />
            </div>
          )}

          {/* Product Modal */}
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-start justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-gradient-to-r from-black via-gray-900 to-black rounded-lg p-8 max-w-3xl w-full my-8">
                <h2 className="heading-metallic-soft text-2xl mb-6">
                  {isBulkEditMode 
                    ? `Bulk Edit Shipping & Payment (${bulkEditProductIds.length} products)` 
                    : editingProduct 
                    ? "Edit Product" 
                    : "Add New Product"}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {!isBulkEditMode && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* SKU */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          SKU <span className="text-red-500">*</span>
                        </label>
                        <input type="text" name="sku" value={formData.sku} onChange={handleInputChange} required disabled={editingProduct} className="input-field" />
                      </div>

                      {/* Name */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Product Name <span className="text-red-500">*</span>
                        </label>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="input-field" />
                      </div>

                      {/* Price */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Price <span className="text-red-500">*</span>
                        </label>
                        <input type="number" step="0.01" name="price" value={formData.price} onChange={handleInputChange} required className="input-field" />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Stock Level <span className="text-red-500">*</span>
                        </label>
                        <input type="number" min="0" step="1" name="stockLevel" value={formData.stockLevel} onChange={handleInputChange} required className="input-field" />
                      </div>

                      {/* Description */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2">Description</label>
                        <textarea name="description" value={formData.description} onChange={handleInputChange} rows="3" className="input-field" />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2">Product Banners</label>
                        {Object.keys(productBanners).length === 0 ? (
                          <div className="rounded border border-dashed border-gray-700 bg-gray-800 px-4 py-4 text-sm text-gray-400">
                            Create product banners in Site Settings to assign them to this product.
                          </div>
                        ) : (
                          <div className="grid gap-3 rounded border border-gray-700 bg-gray-800 p-4 md:grid-cols-2">
                            {Object.entries(productBanners).map(([bannerId, banner]) => (
                              <label key={bannerId} className="flex items-center cursor-pointer rounded border border-gray-700 bg-black/40 px-4 py-3 hover:border-gold-500/50 transition-colors">
                                <input
                                  type="checkbox"
                                  checked={formData.productBannerIds.includes(bannerId)}
                                  onChange={() => handleProductBannerToggle(bannerId)}
                                  className="mr-3"
                                />
                                <div>
                                  <div className="font-medium text-white">{banner.name}</div>
                                  <div className="text-xs text-gray-500">{bannerId}</div>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Gender */}
                      <div>
                        <label className="block text-sm font-medium mb-2">Gender</label>
                        <select name="gender" value={formData.gender} onChange={handleInputChange} className="input-field">
                          {genderOptions.map((option) => (
                            <option key={option} value={option}>
                              {option.charAt(0).toUpperCase() + option.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Status */}
                      <div>
                        <label className="block text-sm font-medium mb-2">Status</label>
                        <select name="status" value={formData.status} onChange={handleInputChange} className="input-field">
                          {statusOptions.map((option) => (
                            <option key={option} value={option}>
                              {option.replace("_", " ").toUpperCase()}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Release Date */}
                      {(formData.status === "coming_soon" || formData.status === "pre_order") && (
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-2">Release Date</label>
                          <input type="date" name="releaseDate" value={formData.releaseDate} onChange={handleInputChange} className="input-field" />
                        </div>
                      )}

                      {/* Categories */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2">
                          Categories <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 rounded border border-gray-700 bg-gray-800 p-4">
                          {categories.map((cat) => (
                            <label key={cat.id} className="flex items-center cursor-pointer hover:text-gold-500 transition-colors">
                              <input
                                type="checkbox"
                                checked={formData.categoryIds.includes(cat.id)}
                                onChange={() => handleCategoryToggle(cat.id)}
                                className="mr-2"
                              />
                              {cat.name}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2">Product Variables</label>
                        {productOptionSets.length === 0 ? (
                          <div className="rounded border border-dashed border-gray-700 bg-gray-800 px-4 py-4 text-sm text-gray-400">
                            Create product form variables in Site Settings to assign selectable options to this product.
                          </div>
                        ) : (
                          <div className="overflow-hidden rounded border border-gray-700 bg-gray-800">
                            <table className="w-full border-collapse">
                              <tbody>
                                {productOptionSets.map((optionSet, index) => (
                                  <tr key={optionSet.id} className={index < productOptionSets.length - 1 ? "border-b border-gray-700" : ""}>
                                    <td className="w-1/3 px-4 py-4 align-top font-medium text-white">{optionSet.name}</td>
                                    <td className="px-4 py-4">
                                      <div className="flex flex-wrap gap-x-6 gap-y-3">
                                        {optionSet.options.map((optionValue) => (
                                          <label key={optionValue} className="flex items-center cursor-pointer text-sm text-gray-200">
                                            <input
                                              type="checkbox"
                                              checked={(formData.productOptionValues[optionSet.id] || []).includes(optionValue)}
                                              onChange={() => handleProductOptionValueToggle(optionSet.id, optionValue)}
                                              className="mr-2"
                                            />
                                            {optionValue}
                                          </label>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Payment and Shipping Methods - Always Visible */}
                  <div className="space-y-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-2">Available Payment Methods</label>
                      {paymentMethods.length === 0 ? (
                        <div className="rounded border border-dashed border-gray-700 bg-gray-800 px-4 py-4 text-sm text-gray-400">
                          Create payment methods in Site Settings before adding products.
                        </div>
                      ) : (
                        <div className="grid gap-3 rounded border border-gray-700 bg-gray-800 p-4 md:grid-cols-2">
                          {paymentMethods.map((method) => (
                            <label key={method.id} className="flex items-center cursor-pointer rounded border border-gray-700 bg-black/40 px-4 py-3 hover:border-gold-500/50 transition-colors">
                              <input
                                type="checkbox"
                                checked={formData.productPaymentMethodIds.includes(method.id)}
                                onChange={() => handleProductPaymentMethodToggle(method.id)}
                                className="mr-3"
                              />
                              <div className="font-medium text-white">{method.name}</div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-2">Available Shipping Methods</label>
                      {shippingMethods.length === 0 ? (
                        <div className="rounded border border-dashed border-gray-700 bg-gray-800 px-4 py-4 text-sm text-gray-400">
                          Create shipping methods in Site Settings before adding products.
                        </div>
                      ) : (
                        <div className="grid gap-3 rounded border border-gray-700 bg-gray-800 p-4 md:grid-cols-2">
                          {shippingMethods.map((method) => (
                            <label key={method.id} className="flex items-center cursor-pointer rounded border border-gray-700 bg-black/40 px-4 py-3 hover:border-gold-500/50 transition-colors">
                              <input
                                type="checkbox"
                                checked={formData.productShippingMethodIds.includes(method.id)}
                                onChange={() => handleProductShippingMethodToggle(method.id)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-white">{method.name}</div>
                                <div className="text-xs text-gray-500">{method.duration} · €{Number(method.price).toFixed(2)}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {!isBulkEditMode && (
                    <>
                      {/* Product Images */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Product Images <span className="text-red-500">*</span>
                          <span className="text-gray-500 text-xs ml-2">(Max 5 images)</span>
                        </label>

                        {imagePreview.length > 0 && (
                          <div className="grid grid-cols-2 gap-4 mb-4 sm:grid-cols-3 lg:grid-cols-5">
                            {imagePreview.map((url, index) => (
                              <div key={index} className="relative group">
                                <img src={url} alt={`Preview ${index + 1}`} className="h-32 w-full rounded border border-gray-700 object-cover" />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveImage(index)}
                                  className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white opacity-0 transition-opacity group-hover:opacity-100"
                                >
                                  ×
                                </button>
                                {index === 0 && <span className="absolute bottom-1 left-1 rounded bg-gold-500 px-2 py-1 text-xs text-black">Primary</span>}
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="rounded border border-gray-700 bg-gray-800 p-4">
                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={openImageUploadDialog}
                              disabled={uploadingImage || formData.imageUrls.length >= 5}
                              className="btn-primary disabled:opacity-50"
                            >
                              {uploadingImage ? "Uploading..." : "Upload New"}
                            </button>
                            <button
                              type="button"
                              onClick={handleBrowseImages}
                              disabled={formData.imageUrls.length >= 5}
                              className="btn-secondary disabled:opacity-50"
                            >
                              Browse Existing
                            </button>
                            <span className="text-sm text-gray-400">
                              {formData.sku ? `${formData.imageUrls.length} / 5 images` : "Enter SKU first to upload into the correct folder"}
                            </span>
                          </div>
                          <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            disabled={uploadingImage || !formData.sku || formData.imageUrls.length >= 5}
                            className="hidden"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="flex justify-end gap-4 pt-6 border-t border-gray-700">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="px-6 py-2 border border-gray-600 rounded hover:bg-gray-800"
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      {isBulkEditMode 
                        ? `Update ${bulkEditProductIds.length} Products` 
                        : editingProduct 
                        ? "Update Product" 
                        : "Create Product"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Image Browser Modal */}
          {showImageBrowser && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-start justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-gradient-to-r from-black via-gray-900 to-black rounded-lg p-8 max-w-6xl w-full my-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="heading-metallic-soft text-2xl">Browse Images</h2>
                  <button
                    type="button"
                    onClick={() => {
                      setShowImageBrowser(false);
                      setSelectedBrowseImages(new Set());
                      setCurrentPath("");
                    }}
                    className="text-gray-400 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>

                {/* Breadcrumb Navigation */}
                <div className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em]">
                  <button
                    onClick={() => navigateToBreadcrumb(-1)}
                    className="text-gold-500 transition-colors duration-200 hover:text-gold-300"
                  >
                    Root
                  </button>
                  {currentPath.split("/").filter(Boolean).map((part, index) => (
                    <React.Fragment key={index}>
                      <span className="text-gray-500">/</span>
                      <button
                        onClick={() => navigateToBreadcrumb(index)}
                        className="text-gold-500 transition-colors duration-200 hover:text-gold-300"
                      >
                        {part}
                      </button>
                    </React.Fragment>
                  ))}
                </div>

                {/* Fallback Notice */}
                {requestedPath && currentPath !== requestedPath && (
                  <div className="mb-4 bg-yellow-900/20 border border-yellow-600/30 rounded p-3 text-sm">
                    <span className="text-yellow-400">ℹ️ </span>
                    <span className="text-gray-300">
                      Folder <code className="text-yellow-300 bg-gray-800 px-2 py-1 rounded">{requestedPath}</code> not found. 
                      Showing {currentPath ? `parent directory` : `root`}.
                    </span>
                  </div>
                )}

                {loadingImages ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400">Loading...</div>
                  </div>
                ) : (
                  <>
                    {/* Navigation Controls */}
                    {currentPath && (
                      <button
                        onClick={navigateUp}
                        className="mb-4 flex items-center gap-2 text-gray-400 hover:text-white"
                      >
                        <span>←</span> Back
                      </button>
                    )}

                    {/* Folders */}
                    {browsableFolders.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-sm font-bold text-gray-400 mb-3">Folders</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                          {browsableFolders.map((folder) => (
                            <button
                              key={folder.path}
                              onClick={() => navigateToFolder(folder.path)}
                              className="flex items-center gap-2 p-3 bg-gray-800 hover:bg-gray-700 rounded border border-gray-700 hover:border-gold-500 text-left transition-all"
                            >
                              <span className="text-2xl">📁</span>
                              <span className="text-sm truncate">{folder.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Files */}
                    {browsableFiles.length > 0 ? (
                      <>
                        <h3 className="text-sm font-bold text-gray-400 mb-3">
                          Images ({browsableFiles.length})
                        </h3>
                        <div className="mb-4 text-sm text-gray-400">
                          Select up to {5 - formData.imageUrls.length} images
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                          {browsableFiles.map((image) => {
                            const isSelected = selectedBrowseImages.has(image.url);
                            const isAlreadyAdded = formData.imageUrls.includes(image.url);

                            return (
                              <div
                                key={image.key}
                                onClick={() => !isAlreadyAdded && toggleBrowseImageSelection(image.url)}
                                className={`relative cursor-pointer rounded border-2 overflow-hidden transition-all ${
                                  isAlreadyAdded
                                    ? "border-gray-700 opacity-50 cursor-not-allowed"
                                    : isSelected
                                    ? "border-gold-500 ring-2 ring-gold-500"
                                    : "border-gray-700 hover:border-gray-500"
                                }`}
                              >
                                <img src={image.url} alt={image.name} className="w-full h-32 object-cover" />
                                <div className="absolute top-2 right-2">
                                  {isAlreadyAdded ? (
                                    <span className="bg-gray-600 text-white text-xs px-2 py-1 rounded">Added</span>
                                  ) : isSelected ? (
                                    <span className="bg-gold-500 text-black text-xs px-2 py-1 rounded font-bold">✓</span>
                                  ) : null}
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                                  <div className="text-white text-xs truncate" title={image.name}>
                                    {image.name}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : browsableFolders.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        No images or folders found in this location.
                      </div>
                    ) : null}

                    {/* Actions */}
                    <div className="flex justify-between items-center pt-6 border-t border-gray-700">
                      <div className="text-sm text-gray-400">{selectedBrowseImages.size} images selected</div>
                      <div className="flex gap-4">
                        <button
                          type="button"
                          onClick={() => {
                            setShowImageBrowser(false);
                            setSelectedBrowseImages(new Set());
                            setCurrentPath("");
                            setRequestedPath("");
                          }}
                          className="px-6 py-2 border border-gray-600 rounded hover:bg-gray-800"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleAddSelectedImages}
                          disabled={selectedBrowseImages.size === 0}
                          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Add {selectedBrowseImages.size > 0 ? `${selectedBrowseImages.size} ` : ""}Images
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
